import { OCRBlock, NormalizedOCRDocument, RawOCRResponse } from './ocrTypes';

function generateBlockId(prefix: string): string {
  return `ocr-${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`;
}

/**
 * Extracts clean string content from raw OCR API response
 */
export function extractTextFromResponse(raw: RawOCRResponse | string | any): string {
  if (!raw) return '';

  let result = '';

  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return extractTextFromResponse(parsed);
    } catch {
      result = raw;
    }
  } else if (raw.response) {
    // Handle { response: "{\"text\":\"...\"}" }
    if (typeof raw.response === 'string') {
      try {
        const inner = JSON.parse(raw.response);
        if (inner && typeof inner.text === 'string') {
          result = inner.text;
        } else {
          result = raw.response;
        }
      } catch {
        result = raw.response;
      }
    } else if (typeof raw.response === 'object' && raw.response.text) {
      result = raw.response.text;
    }
  } else if (typeof raw.text === 'string') {
    result = raw.text;
  } else if (Array.isArray(raw.blocks)) {
    result = raw.blocks.map((b: any) => b.content || b.text || '').join('\n\n');
  } else {
    result = JSON.stringify(raw);
  }

  const trimmed = result.trim();
  if (trimmed === 'undefined' || trimmed === 'null' || trimmed === '{"text":"undefined"}') {
    return '';
  }

  return result;
}

/**
 * Parses markdown table text into structured columns and rows
 */
function parseMarkdownTable(tableText: string): { headers: string[]; rows: string[][] } | null {
  const lines = tableText.trim().split('\n').map((l) => l.trim()).filter(Boolean);
  if (lines.length < 2) return null;

  // Header row
  const headerLine = lines[0];
  if (!headerLine.includes('|')) return null;

  const headers = headerLine
    .split('|')
    .map((c) => c.trim())
    .filter((_, idx, arr) => idx !== 0 || arr[idx] !== '') // handle leading pipe
    .filter((_, idx, arr) => idx !== arr.length - 1 || arr[idx] !== ''); // handle trailing pipe

  // Check if second line is divider (e.g. |---|---|)
  const isDivider = lines[1].includes('-') && lines[1].includes('|');
  const contentStartIdx = isDivider ? 2 : 1;

  const rows: string[][] = [];
  for (let i = contentStartIdx; i < lines.length; i++) {
    const rowLine = lines[i];
    if (!rowLine.includes('|')) continue;
    const cells = rowLine
      .split('|')
      .map((c) => c.trim())
      .filter((_, idx, arr) => idx !== 0 || arr[idx] !== '')
      .filter((_, idx, arr) => idx !== arr.length - 1 || arr[idx] !== '');

    if (cells.length > 0) {
      rows.push(cells);
    }
  }

  if (headers.length === 0 && rows.length === 0) return null;
  return { headers, rows };
}

/**
 * Intelligent OCR Text Parser and AST Block Normalizer
 */
export function normalizeOCRContent(
  rawResponse: RawOCRResponse | string | any,
  sourceMeta?: { fileName?: string; fileSize?: number; durationMs?: number }
): NormalizedOCRDocument {
  const rawText = extractTextFromResponse(rawResponse);
  const blocks: OCRBlock[] = [];

  if (!rawText.trim()) {
    return {
      blocks: [],
      rawText: '',
      metadata: {
        processedAt: new Date().toISOString(),
        sourceFileName: sourceMeta?.fileName,
        sourceFileSize: sourceMeta?.fileSize,
        durationMs: sourceMeta?.durationMs,
      },
    };
  }

  // Regex patterns for LaTeX equations, Markdown tables, and structured headings
  const displayFormulaRegex = /\\\[([\s\S]*?)\\\]|\$\$([\s\S]*?)\$\$/g;
  const tableRegex = /(?:\|[^\n]+\|\r?\n(?:\|[-:\s|]+\|\r?\n)?(?:\|[^\n]+\|\r?\n?)+)/g;

  let currentPos = 0;
  const matches: Array<{ start: number; end: number; type: 'formula' | 'table'; content: string }> = [];

  // 1. Find all display formulas
  let fMatch: RegExpExecArray | null;
  while ((fMatch = displayFormulaRegex.exec(rawText)) !== null) {
    const formulaContent = (fMatch[1] || fMatch[2] || '').trim();
    matches.push({
      start: fMatch.index,
      end: fMatch.index + fMatch[0].length,
      type: 'formula',
      content: formulaContent,
    });
  }

  // 2. Find all markdown tables
  let tMatch: RegExpExecArray | null;
  while ((tMatch = tableRegex.exec(rawText)) !== null) {
    matches.push({
      start: tMatch.index,
      end: tMatch.index + tMatch[0].length,
      type: 'table',
      content: tMatch[0],
    });
  }

  // Sort matches by appearance in source text
  matches.sort((a, b) => a.start - b.start);

  // Helper to parse intervening text segments for headings, inline math, and paragraphs
  const processTextSegment = (segment: string) => {
    const trimmed = segment.trim();
    if (!trimmed) return;

    const paragraphs = trimmed.split(/\n\s*\n/);
    paragraphs.forEach((para) => {
      const pTrimmed = para.trim();
      if (!pTrimmed) return;

      // Check if paragraph is a Heading (e.g. # Title, ## Subtitle, or numbered like "1. Objectives")
      const headingMatch = pTrimmed.match(/^(#{1,4})\s+(.+)$/);
      const numberedHeadingMatch = pTrimmed.match(/^(\d+\.?\s+[A-Za-z\s]{3,40})$/);

      if (headingMatch) {
        const level = headingMatch[1].length as 1 | 2 | 3 | 4;
        blocks.push({
          id: generateBlockId('hdg'),
          type: 'heading',
          content: headingMatch[2].trim(),
          level,
        });
      } else if (numberedHeadingMatch) {
        blocks.push({
          id: generateBlockId('hdg'),
          type: 'heading',
          content: numberedHeadingMatch[1].trim(),
          level: 2,
        });
      } else {
        // Normal paragraph text (preserve inline formulas \( ... \) inside text block)
        blocks.push({
          id: generateBlockId('txt'),
          type: 'text',
          content: pTrimmed,
        });
      }
    });
  };

  // 3. Reconstruct ordered stream
  for (const match of matches) {
    if (match.start > currentPos) {
      const textBefore = rawText.substring(currentPos, match.start);
      processTextSegment(textBefore);
    }

    if (match.type === 'formula') {
      blocks.push({
        id: generateBlockId('fml'),
        type: 'formula',
        latex: match.content,
        displayMode: true,
      });
    } else if (match.type === 'table') {
      const parsedTable = parseMarkdownTable(match.content);
      if (parsedTable) {
        blocks.push({
          id: generateBlockId('tbl'),
          type: 'table',
          headers: parsedTable.headers,
          rows: parsedTable.rows,
        });
      } else {
        blocks.push({
          id: generateBlockId('txt'),
          type: 'text',
          content: match.content,
        });
      }
    }

    currentPos = match.end;
  }

  // Trailing text
  if (currentPos < rawText.length) {
    const trailingText = rawText.substring(currentPos);
    processTextSegment(trailingText);
  }

  return {
    blocks,
    rawText,
    metadata: {
      processedAt: new Date().toISOString(),
      sourceFileName: sourceMeta?.fileName,
      sourceFileSize: sourceMeta?.fileSize,
      durationMs: sourceMeta?.durationMs,
    },
  };
}
